import { useEffect, useRef, useCallback } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot, $createParagraphNode, $createTextNode,
  FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND, COMMAND_PRIORITY_LOW,
} from 'lexical';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useState } from 'react';
import './report/blocks/LexicalText/lexicalStyles.css';

const theme = {
  text: { bold: 'fw-bold', italic: 'fst-italic', underline: 'text-decoration-underline' },
};

/* Exposes the Lexical editor instance to a caller-provided ref, so an
   element outside the composer (e.g. a sidebar "insert variable" row) can
   focus the editor and insert text at its last cursor position. */
function EditorRefPlugin({ editorRef }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!editorRef) return;
    editorRef.current = editor;
    return () => { editorRef.current = null; };
  }, [editor, editorRef]);
  return null;
}

/* Populate the editor with plain text on first render */
function HydratePlugin({ text }) {
  const [editor] = useLexicalComposerContext();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (!text) return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      text.split(/\n\n+/).forEach(block => {
        const para = $createParagraphNode();
        block.split('\n').forEach((line, i, arr) => {
          para.append($createTextNode(line));
          // No LineBreakNode import needed for simple single-para templates
        });
        root.append(para);
      });
    });
  }, []); // eslint-disable-line
  return null;
}

/* Minimal bold/italic toolbar */
function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const sel = $getSelection();
        if ($isRangeSelection(sel)) {
          setBold(sel.hasFormat('bold'));
          setItalic(sel.hasFormat('italic'));
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  const btn = (active) => ({
    border: `1px solid ${active ? '#198754' : '#dee2e6'}`,
    background: active ? '#198754' : '#fff',
    color: active ? '#fff' : '#495057',
    borderRadius: 3, padding: '1px 8px', cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 600,
  });

  return (
    <div className="d-flex gap-1 mb-1 pb-1 border-bottom">
      <button style={btn(bold)} onMouseDown={e => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'); }}>B</button>
      <button style={{ ...btn(italic), fontStyle: 'italic' }} onMouseDown={e => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'); }}>I</button>
    </div>
  );
}

export default function MinimalLexicalEditor({ value, onChange, placeholder, minHeight = 80, editorRef }) {
  const composerKey = useRef(value ?? '').current; // stable key per mount

  const handleChange = useCallback((editorState) => {
    editorState.read(() => {
      const text = $getRoot().getTextContent();
      onChange?.(text);
    });
  }, [onChange]);

  const initialConfig = {
    namespace: 'minimal-lexical',
    theme,
    onError: console.error,
  };

  return (
    <div className="border rounded" style={{ background: '#fff', fontSize: '0.88rem' }}>
      <LexicalComposer key={composerKey} initialConfig={initialConfig}>
        <HydratePlugin text={value} />
        <EditorRefPlugin editorRef={editorRef} />
        <HistoryPlugin />
        <div className="px-2 pt-2">
          <Toolbar />
        </div>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="lexical-editable px-2 pb-2"
              style={{ minHeight, outline: 'none', lineHeight: 1.6 }}
            />
          }
          placeholder={
            <div className="px-2 text-muted" style={{ fontSize: '0.85rem', pointerEvents: 'none', position: 'absolute', top: 52 }}>
              {placeholder}
            </div>
          }
        />
        <OnChangePlugin onChange={handleChange} />
      </LexicalComposer>
    </div>
  );
}
